import type { InputType, Stage } from '../kit/stage';

/**
 * Editing a case after intake. System test handover 2.4.
 *
 * A case's route is decided by its input type and its item. They were fixed
 * once any kit had left Intake; since the lab's request of 18 October 2026
 * either may change at any stage, and so may the item on a tooth. Such a save
 * asks a question of its own first, and can be undone for a moment after. A
 * kit stays where it is and goes on by its new route, or, at a bench that
 * route does not pass, is sent on by hand as a flagged kit is.
 */

/** What decides a case's route and its bill, as the edit form holds it. */
export interface CaseItems {
  input: InputType;
  product: string;
  /** Each marked tooth with the item on it. */
  teeth: readonly { tooth: string; product: string }[];
}

/**
 * What an edit changes of them: the input type, the case's item, and how many
 * of the teeth it keeps are given another item. A tooth marked or taken off is
 * a change of the chart, not of an item.
 */
export interface ItemChanges {
  input: boolean;
  product: boolean;
  teeth: number;
}

export function itemChanges(before: CaseItems, after: CaseItems): ItemChanges {
  const was = new Map(before.teeth.map((each) => [each.tooth, each.product]));
  const teeth = after.teeth.filter((each) => {
    const old = was.get(each.tooth);
    return old !== undefined && old !== each.product;
  }).length;
  return { input: before.input !== after.input, product: before.product !== after.product, teeth };
}

/** Whether a save changes the input type or an item: the saves that ask first and can be undone. */
export function changesItems(changes: ItemChanges): boolean {
  return changes.input || changes.product || changes.teeth > 0;
}

/**
 * Where a case goes once a save has changed its route, said in the question
 * before it is saved:
 *
 * - `next`: its kits are at a bench the new route passes, and go on from there
 *   to `next`;
 * - `off`: they are at a bench the new route does not pass, so whoever
 *   finishes the case there chooses where it goes;
 * - `apart`: its kits are at different benches, and each goes on by the new
 *   route from where it is.
 *
 * Null when nothing needs saying: every kit is still at Intake, the route is
 * the same road it was, or the case is at the end of it.
 */
export type RouteAfterEdit =
  | { kind: 'next'; at: Stage; next: Stage }
  | { kind: 'off'; at: Stage }
  | { kind: 'apart' };

export function routeAfterEdit(
  stages: readonly Stage[],
  before: readonly Stage[],
  after: readonly Stage[],
): RouteAfterEdit | null {
  const [first, ...rest] = stages;
  if (first === undefined || stages.every((stage) => stage === 'intake')) return null;
  if (before.length === after.length && before.every((stage, index) => after[index] === stage)) {
    return null;
  }
  if (rest.some((stage) => stage !== first)) return { kind: 'apart' };

  const index = after.indexOf(first);
  if (index === -1) return { kind: 'off', at: first };
  const next = after[index + 1];
  return next === undefined ? null : { kind: 'next', at: first, next };
}
